import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import PagedSelect from "@/components/ui/custom/PageSelect";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import "@/styles/loa.css";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { LOADetail, RequestLOAUserAssign } from "@/models/loa";
import { getUsersAsync, getDetailsAsync, setUserAssignAsync } from "@/services/loaService";


//Types
type Option = { value: number; label: string; };
type Params = { sysId: string };

// Grid
const policyColumns: GridColDef[] = [
    { field: "policyId", headerName: "Policy ID", width: 140 },
    { field: "policyNum", headerName: "Policy Number", flex: 1, minWidth: 250 },
    { field: "policyTxnStatus", headerName: "Policy Status", flex: 1, minWidth: 250 }
];

const claimColumns: GridColDef[] = [
    { field: "claimId", headerName: "Claim ID", width: 140 },
    { field: "claimNum", headerName: "Claim Number", flex: 1, minWidth: 250 },
    { field: "claimStatus", headerName: "Claim Status", flex: 1, minWidth: 250 }
];

const LOA: React.FC = () => {
    const navigate = useNavigate();
    const [alert, setAlert] = useState({ type: "", message: "" });
    const [processing, setProcessing] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState<Option | null>(null);
    const [selectedClaim, setSelectedClaim] = useState<Option | null>(null);
    const [disableReAssign, setDisableReAssign] = useState(false);
    const [options, setOptions] = useState<Option[]>([]);
    const { sysId } = useParams<Params>();
    const [details, setDetails] = useState<LOADetail | undefined>(undefined);

    //Grid
    const [active, setActive] = useState("policies")
    const [loading, setLoading] = useState(false);

    function handleExceptionMessage(e: unknown, customMsg: string) {
        if (axios.isAxiosError(e)) {
            const d = e.response?.data;
            const serverMessage = typeof d === "string" ? d
                : d?.errors ? Object.values(d.errors).flat().join(", ")
                    : d?.title || "Unable to connect to server";

            setAlert({ type: "error", message: serverMessage });
            return;
        }
        setAlert({ type: "error", message: e instanceof Error ? e.message : customMsg });
    }

    // Bind users to dropdown
    async function loadUsers() {
        try {
            setAlert({ type: "", message: "" });

            const res = await getUsersAsync();
            setOptions(
                Array.isArray(res?.data)
                    ? res.data.map((u: any) => ({ value: u?.userId, label: u?.displayName })).filter(o => o.value != null && o.label)
                    : []
            );

        } catch (e: unknown) {
            setOptions([]);
            handleExceptionMessage(e, "Failed to load users");
        }
    }

    // Fetch details for Policy & Claim
    async function loadDetailsAsync() {
        try {
            setLoading(true);
            setAlert({ type: "", message: "" });

            const res = await getDetailsAsync(sysId ?? "");
            setDetails(res.data ?? null);
            setDisableReAssign((res?.data?.sNowStatus ?? false).trim() === "7")
            setOptions(prev => (prev ?? []).filter(f => f.value !== res?.data?.sourceUser?.userId));

        } catch (e: unknown) {
            setDetails(undefined);
            setDisableReAssign(true);
            handleExceptionMessage(e, "Failed to load Policy & Claim details");
        } finally {
            setLoading(false);
        }
    }

    // Assign new user to Policy & Claim
    async function userAssignAsync(request: RequestLOAUserAssign) {
        try {
            setDisableReAssign(true);
            setProcessing(true);
            setAlert({ type: "", message: "" });
            const res = await setUserAssignAsync(request);

            const isReassigned =
                request?.targetClaimUserId == null &&
                request?.targetPolicyUserId == null;

            setAlert({
                type: "success",
                message: isReassigned
                    ? "LOA transaction completed successfully"
                    : "Reassignment process completed successfully",
            }
            );

            setProcessing(false);
        } catch (e: unknown) {
            handleExceptionMessage(e, "Failed to re-assign user");
            setProcessing(false);
        }
    }

    function Policies() {
        return (
            <CardContent className="space-y-6">
                <CardContent className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700">Re-Assign To</label>
                    <PagedSelect
                        allOptions={options}
                        value={selectedPolicy}
                        onChange={setSelectedPolicy}
                        disabled={(details?.sNowStatus ?? "").trim() === "7" ||
                            (details?.policies?.length ?? 0) === 0
                        }
                    />
                </CardContent>

                {(details?.sNowStatus ?? "").trim() === "7" && (
                    (details?.assignedPolicyUser?.displayName ?? "").trim() === "" ? (
                        <div className="block w-full text-red-600">
                            No re-assignment was done, no further action required.
                        </div>
                    ) : (
                        <div className="block w-full text-red-600">
                            Following policies have been assigned from{" "}
                            {(details?.sourceUser?.displayName ?? "").trim()} to{" "}
                            {(details?.assignedPolicyUser?.displayName ?? "").trim()}, no further action required.
                        </div>
                    )
                )}

                <fieldset className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">

                    <legend className="px-2 text-sm font-semibold text-slate-900">
                        Policies
                    </legend>

                    <DataGrid
                        rows={details?.policies}
                        columns={policyColumns}
                        loading={loading}
                        pagination
                        getRowId={(row) => row.policyId + '~' + row.policyNum}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 5 },
                            },
                        }}
                        pageSizeOptions={[5, 10, 25, 50, 100]}

                    />

                </fieldset>
            </CardContent>
        );
    }

    function Claims() {
        return (
            <CardContent className="space-y-6">
                <CardContent className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700">Re-Assign To</label>
                    <PagedSelect
                        allOptions={options}
                        value={selectedClaim}
                        onChange={setSelectedClaim}
                        disabled={(details?.sNowStatus ?? "").trim() === "7" ||
                            (details?.claims?.length ?? 0) === 0
                        }
                    />
                </CardContent>

                {(details?.sNowStatus ?? "").trim() === "7" && (
                    (details?.assignedClaimUser?.displayName ?? "").trim() === "" ? (
                        <div className="block w-full text-red-600">
                            No re-assignment was done, no further action required.
                        </div>
                    ) : (
                        <div className="block w-full text-red-600">
                            Following claims have been assigned from{" "}
                            {(details?.sourceUser?.displayName ?? "").trim()} to{" "}
                            {(details?.assignedClaimUser?.displayName ?? "").trim()}, no further action required.
                        </div>
                    )
                )}

                <fieldset className="mt-6 mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <legend className="px-2 text-sm font-semibold text-slate-900">
                        Claims
                    </legend>
                    <DataGrid
                        rows={details?.claims}
                        columns={claimColumns}
                        loading={loading}
                        pagination
                        getRowId={(row) => row.claimId + '~' + row.claimNum}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 5 },
                            },
                        }}
                        pageSizeOptions={[5, 10, 25, 50, 100]}
                    />
                </fieldset>

            </CardContent>
        );
    }

    function handleReAssign() {
        const ok = window.confirm("Are you sure you want to proceed?");
        if (!ok) return;

        const res: RequestLOAUserAssign = {
            sourceUserId: details?.sourceUser?.userId,
            sysId: sysId ?? "",
            targetPolicyUserId: selectedPolicy?.value ?? null,
            targetClaimUserId: selectedClaim?.value ?? null,
            managerId: details?.managerId
        }
        userAssignAsync(res);
    }
    useEffect(() => {
        loadUsers();
        loadDetailsAsync();
    }, []);

    return (
        <>
            <div className="w-full flex justify-center px-6 py-8">
                <div className="w-full max-w-6xl">

                    <Card className="border-slate-200 shadow-lg rounded-2xl overflow-hidden">
                        <CardHeader className="pb-4">
                            <div className="flex items-center justify-between gap-4">

                                <div className="text-center flex-1">
                                    <CardTitle className="text-blue-700 text-base sm:text-lg">
                                        Leave Of Absence
                                    </CardTitle>
                                </div>

                            </div>
                            <Separator className="mt-4" />
                        </CardHeader>


                        <div style={styles.tabButtons}>
                            <button
                                style={active === "policies" ? styles.activeTab : styles.tab}
                                onClick={() => setActive("policies")}
                            >
                                Policies
                            </button>

                            <button
                                style={active === "claims" ? styles.activeTab : styles.tab}
                                onClick={() => setActive("claims")}
                            >
                                Claims
                            </button>

                            {((details?.sNowStatus ?? "").trim() !== "7" &&
                                (details === undefined ||
                                    (
                                        (details?.assignedPolicyUser?.displayName ?? "") === "" &&
                                        (details?.assignedClaimUser?.displayName ?? "") === ""
                                    )
                                )
                            ) && (
                                    <div className="w-full text-right text-red-600">
                                        Action required: Reassign work during LOA of {details?.sourceUser?.displayName}
                                    </div>
                                )}
                        </div>

                        {/* Tab Content */}
                        <div style={styles.tabContent}>
                            {active === "policies" && <Policies />}
                            {active === "claims" && <Claims />}
                        </div>


                        {alert.message && (
                            <div
                                className={`px-4 py-2 rounded text-sm ${alert.type === "success"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                    }`}
                            >
                                {alert.message}
                            </div>
                        )}

                        <CardContent className="flex items-center gap-4 mt-5 mb-10 justify-end">
                            <Button
                                className="min-w-[140px] gap-2 btn btn-search"
                                variant="outline"
                                onClick={handleReAssign}
                                disabled={disableReAssign}
                            >
                                {processing && (
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                )}
                                Re-assign & Close
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>


        </>
    );
}



const styles = {
    tabButtons: {
        display: "flex",
        gap: "10px",
        marginLeft: "5px",
        marginRight: "5px",
    },
    tab: {
        padding: "8px 16px",
        border: "1px solid #ddd",
        background: "#fff",
        cursor: "pointer",
        borderRadius: "4px"
    },
    activeTab: {
        padding: "8px 16px",
        border: "1px solid rgb(87, 106, 236)",
        background: "rgb(87, 106, 236)",
        color: "#fff",
        cursor: "pointer",
        borderRadius: "4px"
    },
    tabContent: {
        padding: "10px",
        border: "1px solid #eee",
        borderRadius: "8px",
        background: "#fafafa",
    },
};

export default LOA;